import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { Link } from 'react-router-dom';
import { brandIntro, milestones, testimonials, contactInfo, statsData } from '@/mocks/about';

export default function About() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <nav className="bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-700 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-12 md:py-16" aria-label="品牌介绍">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-5">
                {brandIntro.title}
              </h1>
              <p className="text-lg text-foreground-600 font-heading mb-6">
                {brandIntro.subtitle}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {brandIntro.description}
              </p>
              <div className="bg-background-100 rounded-lg p-6 text-left">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  <strong className="text-foreground-950">我们的使命：</strong>{brandIntro.mission}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">我们的愿景：</strong>{brandIntro.vision}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
              {statsData.map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-center text-center p-5 rounded-lg bg-background-100"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <span className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                    {stat.value}
                  </span>
                  <span className="text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100" id="milestones" aria-label="发展历程">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="flex flex-col gap-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-10 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-start gap-4 pl-10 md:pl-0">
                      <div className={`md:hidden absolute left-4 w-2 h-2 rounded-full bg-primary-500 -translate-x-[3px] mt-2`} />
                      <div className={`hidden md:block absolute left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-2 ${index % 2 === 0 ? 'md:left-1/2' : 'md:left-1/2'}`} />
                      <div className={`${index % 2 === 0 ? 'md:text-right md:ml-auto' : 'md:text-left'} hidden md:block flex-1`}>
                        {index % 2 === 0 ? (
                          <div className="p-4 rounded-lg bg-background-50">
                            <span className="text-xs font-semibold text-primary-500">{milestone.year}</span>
                            <h3 className="text-sm font-semibold text-foreground-950 mt-1 mb-1">{milestone.title}</h3>
                            <p className="text-xs text-foreground-500 leading-relaxed">{milestone.description}</p>
                          </div>
                        ) : (
                          <div />
                        )}
                      </div>
                      <div className={`${index % 2 === 0 ? 'md:text-right md:mr-auto' : 'md:text-left md:ml-auto'} hidden md:block flex-1`}>
                        {index % 2 === 1 ? (
                          <div className="p-4 rounded-lg bg-background-50">
                            <span className="text-xs font-semibold text-primary-500">{milestone.year}</span>
                            <h3 className="text-sm font-semibold text-foreground-950 mt-1 mb-1">{milestone.title}</h3>
                            <p className="text-xs text-foreground-500 leading-relaxed">{milestone.description}</p>
                          </div>
                        ) : (
                          <div />
                        )}
                      </div>
                    </div>
                    <div className="md:hidden pl-10 pr-4">
                      <div className="p-4 rounded-lg bg-background-50">
                        <span className="text-xs font-semibold text-primary-500">{milestone.year}</span>
                        <h3 className="text-sm font-semibold text-foreground-950 mt-1 mb-1">{milestone.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="testimonials" aria-label="用户口碑">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              来自真实用户的评价与反馈
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-xs ${
                            i < t.rating ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100" id="contact" aria-label="联系方式">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              欢迎通过以下方式与我们取得联系
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                  {contactInfo.email}
                </a>
              </div>
              <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-3">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h3>
                <a href={`tel:${contactInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                  {contactInfo.phone}
                </a>
              </div>
              <div className="flex flex-col items-center text-center p-6 rounded-lg bg-background-50 sm:col-span-2 lg:col-span-1">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mb-3">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h3>
                <p className="text-sm text-foreground-600">{contactInfo.workingHours}</p>
              </div>
            </div>
            <div className="mt-6 p-5 rounded-lg bg-background-50 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-foreground-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-primary-500"></i>
                </div>
                {contactInfo.address}
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于樱花视频',
              description: brandIntro.description,
              mainEntity: {
                '@type': 'Organization',
                name: '樱花视频',
                description: brandIntro.description,
                contactPoint: {
                  '@type': 'ContactPoint',
                  telephone: contactInfo.phone,
                  email: contactInfo.email,
                  contactType: 'customer service',
                },
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}